/**
 * Deterministic maths for the simulation (design.md B12.2).
 *
 * JS engines are only guaranteed to agree bit-for-bit on + - * / and sqrt.
 * Math.sin/cos/atan2/exp/pow may differ between Chrome, Firefox, Safari and
 * Node, which would break replays, ghosts, rollback netcode and server-side
 * lap verification. So the sim uses these polynomial versions instead,
 * built only from + - * / and Math.floor (which is exact).
 *
 * Rule: nothing under src/sim may call Math.sin, Math.cos, Math.atan2,
 * Math.exp, Math.pow, Math.random, Date or performance.
 */

export const PI = 3.141592653589793;
export const TAU = 6.283185307179586;
export const HALF_PI = 1.5707963267948966;

/** Wrap an angle to [-PI, PI). */
export function wrapAngle(a: number): number {
  return a - TAU * Math.floor((a + PI) / TAU);
}

// Taylor coefficients for sin on [-PI/2, PI/2]; error < 1e-11 there.
const S3 = -1 / 6;
const S5 = 1 / 120;
const S7 = -1 / 5040;
const S9 = 1 / 362880;
const S11 = -1 / 39916800;
const S13 = 1 / 6227020800;
const S15 = -1 / 1307674368000;

function sinCore(x: number): number {
  const x2 = x * x;
  return x * (1 + x2 * (S3 + x2 * (S5 + x2 * (S7 + x2 * (S9 + x2 * (S11 + x2 * (S13 + x2 * S15)))))));
}

export function dsin(a: number): number {
  const x = wrapAngle(a);
  if (x > HALF_PI) return sinCore(PI - x);
  if (x < -HALF_PI) return sinCore(-PI - x);
  return sinCore(x);
}

export function dcos(a: number): number {
  return dsin(a + HALF_PI);
}

// atan on |x| <= tan(PI/16) after two argument halvings; error < 1e-13.
function atanSmall(x: number): number {
  const x2 = x * x;
  return x * (1 + x2 * (-1 / 3 + x2 * (1 / 5 + x2 * (-1 / 7 + x2 * (1 / 9 + x2 * (-1 / 11 + x2 * (1 / 13 + x2 * (-1 / 15))))))));
}

function datan(x: number): number {
  // atan(x) = 2·atan(x / (1 + sqrt(1 + x²))), applied until |x| is small.
  let k = 1;
  let v = x;
  while (v > 0.19 || v < -0.19) {
    v = v / (1 + Math.sqrt(1 + v * v));
    k *= 2;
  }
  return k * atanSmall(v);
}

/** Deterministic atan2(y, x) in (-PI, PI]. */
export function datan2(y: number, x: number): number {
  if (x > 0) return datan(y / x);
  if (x < 0) return y >= 0 ? datan(y / x) + PI : datan(y / x) - PI;
  if (y > 0) return HALF_PI;
  if (y < 0) return -HALF_PI;
  return 0;
}

export function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

export function sign(v: number): number {
  return v > 0 ? 1 : v < 0 ? -1 : 0;
}

/** FNV-1a over the IEEE-754 bytes of a list of numbers: a state checksum. */
export function hashNumbers(values: readonly number[], seed = 0x811c9dc5): number {
  const buf = new DataView(new ArrayBuffer(8));
  let h = seed >>> 0;
  for (const v of values) {
    buf.setFloat64(0, v);
    for (let i = 0; i < 8; i++) {
      h ^= buf.getUint8(i);
      h = Math.imul(h, 0x01000193) >>> 0;
    }
  }
  return h >>> 0;
}
